import { getDeviceDetail, getProductInfo } from '@/api';
import { editDevices, IDeviceDetail } from '@/api/device';
import { AdvanceBreadcrumb } from '@/components/Breadcrumb';
import useAuthButton from '@/hooks/useAuthButton';
import { useRequest } from 'ahooks';
import { Form, Input, InputNumber, Modal, Tabs, Typography } from 'antd';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DeviceAlarmTab from './components/DeviceAlarmTab';
import DeviceInfoTab from './components/DeviceInfoTab';
import DeviceStatisticsTab from './components/DeviceStatisticsTab';
import { Alarm, Channel, StreamPlay } from './components/Ipc';
import MapFormItems from './components/MapFormItems';
import { DetailTab1 } from './components/Tab1';
import { DetailTab2 } from './components/Tab2';
import { DetailTab3 } from './components/Tab3';
import Tab4 from './components/Tab4';
import Tab5 from './components/Tab5';
import './style.less';
import { ProductInfo } from './types';

const { Text } = Typography;

export interface DeviceDetailProps {}
interface EditorModalRef {
  open: (data: { code: string; label: string; value?: string; detail?: IDeviceDetail }) => void;
}
const EditorModal = forwardRef<EditorModalRef, { onSuccess: () => void }>(({ onSuccess }, ref) => {
  const params = useParams<{ id: string }>();
  const [data, setData] = useState<{ visible: boolean; code?: string; label?: string; value?: string }>({ visible: false });
  const [form] = Form.useForm();
  useImperativeHandle(ref, () => ({
    open: ({ code, label, value, detail }) => {
      console.log({ code, label, value });
      form.setFieldsValue({ ...detail, [code]: value });
      setData({ code, label, value, visible: true });
    },
  }));

  const { run, loading } = useRequest(editDevices, {
    manual: true,
    onSuccess: () => {
      setData({ visible: false });
      onSuccess();
    },
  });

  return (
    <Modal
      title="编辑"
      open={data.visible}
      okButtonProps={{ loading }}
      onCancel={() => setData({ visible: false })}
      okText="保存"
      cancelText="取消"
      onOk={() => {
        const toData = form.getFieldsValue();
        run({ 
          ...params, 
          ...toData, 
          ...(toData.lon ? { lon: String(toData.lon), lat: String(toData.lat) } : {}),
          ...(toData.port !== undefined ? { port: String(toData.port) } : {}),
        });
      }}
    >
      <Form layout="horizontal" form={form} className="mt10">
        {data.code === 'location' ? (
          <MapFormItems></MapFormItems>
        ) : data.code === 'port' ? (
          <Form.Item name={data.code} label={data.label} rules={[{ type: 'number', message: `请输入有效的${data.label}` }]}>
            <InputNumber placeholder={`请输入${data.label}`} style={{ width: '100%' }} />
          </Form.Item>
        ) : (
          <Form.Item name={data.code} label={data.label} required={data.code === 'name'} rules={[{ required: data.code === 'name', message: `请输入${data.label}` }]}>
            <Input placeholder={`请输入${data.label}`} allowClear></Input>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
});


export const DeviceDetail: React.FC<DeviceDetailProps> = () => {
  const [searchParams] = useSearchParams();
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { checkPermissionDisabled } = useAuthButton();

  const [activeKey, setActiveKey] = useState('1'); // 默认显示设备信息tab
  const [detail, setDetail] = useState<IDeviceDetail>();

  useEffect(() => {
    const trigger = decodeURIComponent(searchParams.get('trigger') || '');
    const mapping: { [x: string]: string } = {
      设备数据触发: '2', // 属性tab
      设备事件触发: '3', // 事件记录tab
      子设备触发: '5', // 子设备tab（如果有）
    };
    trigger && mapping[trigger] && setActiveKey(mapping[trigger]);
  }, []);

  const loadDetail = useCallback(() => {
    getDeviceDetail(params.id!).then((resp) => {
      if (resp.success) {
        setDetail(resp.result);
        loadProductInfo(resp.result);
      }
    });
  }, [params]);

  // useEffect(() => {}, [params.id]);

  useEffect(() => {
    loadDetail();

    localStorage.setItem('title', '设备详情');
    document.title = '设备详情';
  }, []);

  // 获取产品详情
  const [productInfo, setProductInfo] = useState<ProductInfo>();
  const loadProductInfo = useCallback(
    (detail?: IDeviceDetail) => {
      getProductInfo<ProductInfo>(detail?.product_id!).then((resp) => {
        if (resp.success) {
          setProductInfo(resp.result);
        }
      });
    },
    [params],
  );

  const modalRef = useRef<EditorModalRef>(null);

  return (
    <>
      <AdvanceBreadcrumb
        style={{height: '60px', minHeight: '60px', paddingTop: '0px', paddingBottom: '0px', background:'white'}}
        hasBack
        onBackTo={`/gateway/device/manage?${searchParams.toString()}`}
        title={
          <div className="flex-center" style={{marginTop: '-6px'}}>
            <Text style={{ fontSize: 20 }} >
              {detail?.name || '设备详情'}
            </Text>
          </div>
        }
        describe=""
        // background={require('@/assets/images/product-background.png')}
        breadcrumb={[
          { label: '设备管理', to: `/gateway/device/manage?${searchParams.toString()}` },
          { label: `设备详情 [ ${detail?.name || ''} ]`, to: '' },
        ]}
      />
      <EditorModal
        ref={modalRef}
        onSuccess={() => {
          loadDetail();
        }}
      ></EditorModal>
      {!detail?.is_ipc ? (
        <Tabs
          className="device-property"
          activeKey={activeKey}
          onChange={(activeKey) => setActiveKey(activeKey)}
          items={[
            {
              key: '1',
              label: '设备信息',
              children: (
                <DeviceInfoTab
                  detail={detail}
                  productInfo={productInfo}
                  navigate={navigate}
                  deviceId={params.id!}
                  onRefresh={loadDetail}
                />
              ),
            },
            {
              key: '2',
              label: '属性',
              children: <DetailTab1 product_id={productInfo?.id}></DetailTab1>,
            },
            {
              key: '3',
              label: '事件记录',
              children: <DetailTab2 events={productInfo?.events || []}></DetailTab2>,
            },
            {
              key: '4',
              label: '服务记录',
              children: <DetailTab3 actions={productInfo?.actions || []}></DetailTab3>,
            },
            ...(productInfo?.node_type === '网关设备' && !checkPermissionDisabled('gateway:device:subdevice')
              ? [
                  {
                    key: '5',
                    label: '子设备',
                    children: <Tab4 id={params.id!}></Tab4>,
                  },
                ]
              : []),
            {
              key: '6',
              label: '设备调试',
              children: <Tab5 detail={detail} productInfo={productInfo} loadDetail={loadDetail}></Tab5>,
            },
            {
              key: '7',
              label: '数据统计',
              children: <DeviceStatisticsTab deviceId={params.id!} />,
            },
            {
              key: '8',
              label: '告警',
              children: <DeviceAlarmTab deviceId={params.id!} />,
            },
          ]}
        />
      ) : (
        <Tabs
          className="device-property"
          activeKey={activeKey}
          onChange={(activeKey) => setActiveKey(activeKey)}
          items={[
            {
              key: '1',
              label: '设备信息',
              children: (
                <DeviceInfoTab
                  detail={detail}
                  productInfo={productInfo}
                  navigate={navigate}
                  deviceId={params.id!}
                  onRefresh={loadDetail}
                />
              ),
            },
            { key: '2', label: '设备通道', children: <Channel device_id={params.id!}></Channel> },
            { key: '3', label: '设备直播', children: <StreamPlay device_id={params.id!}></StreamPlay> },
            { key: '4', label: '设备告警', children: <Alarm device_id={params.id!}></Alarm> },
            { key: '5', label: '数据统计', children: <DeviceStatisticsTab deviceId={params.id!} /> },
          ]}
        />
      )}
    </>
  );
};

export default DeviceDetail;
